import { FC, useState } from "react";
import { storageKey } from "../../../shared/models/brandVariants";

/*
    The canvas of a brand asset tile: the file itself (an image, or a video for the
    Image/Video Library), a download button, and a light/dark backdrop toggle. The logo tile
    grid and the asset sections behind `brand-identity-v2` both use it.
*/

interface Props {
  url: string;
  label: string;
  /** The asset's media type; "video" plays the file instead of showing it as an image. */
  mediaType?: string;
  /** Opens on a dark backdrop, for a reverse (white) mark. */
  startDark: boolean;
  /** Reports the file's own size once it has loaded. */
  onNaturalSize?: (width: number, height: number) => void;
  /** False for a file picked on the page and not uploaded yet: there's nothing to download. */
  downloadable?: boolean;
}

export const baseName = (value: string | null) => {
  if (!value) return "";
  const path = value.split("?")[0];
  return decodeURIComponent(path.substring(path.lastIndexOf("/") + 1));
};

// Reverse marks are invisible on a light canvas — open those tiles on dark.
export const prefersDarkCanvas = (tag: string | null, name: string | null) => {
  const haystack = `${tag ?? ""} ${name ?? ""}`.toUpperCase();
  return haystack.includes("WHITE") || haystack.includes("REVERSE");
};

const download = async (url: string, label: string) => {
  const filename = baseName(storageKey(url)) || label;
  try {
    const response = await fetch(url);
    if (!response.ok) throw new Error(String(response.status));
    const href = URL.createObjectURL(await response.blob());
    const link = document.createElement("a");
    link.href = href;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(href);
  } catch {
    // download= is ignored cross-origin and S3 can refuse the fetch outright,
    // so fall back to handing the presigned URL to the browser.
    window.open(url, "_blank", "noopener");
  }
};

const BrandAssetPreview: FC<Props> = ({ url, label, mediaType = "image", startDark, onNaturalSize, downloadable = true }) => {
  const [broken, setBroken] = useState(false);
  const [darkOverride, setDarkOverride] = useState<boolean | null>(null);
  const onDark = darkOverride ?? startDark;

  return (
    <div className={`brand-tile-canvas ${onDark ? "on-dark" : ""}`}>
      {url && !broken && mediaType === "video" ? (
        <video src={url} aria-label={label} controls muted preload="metadata" onError={() => setBroken(true)} />
      ) : url && !broken ? (
        <img
          src={url}
          alt={label}
          onLoad={(e) => onNaturalSize?.(e.currentTarget.naturalWidth, e.currentTarget.naturalHeight)}
          onError={() => setBroken(true)}
        />
      ) : (
        <div className="brand-tile-fallback">
          <i className="bi bi-exclamation-triangle d-block mb-1"></i>
          {mediaType === "video" ? "Couldn't load video" : "Couldn't load image"}
        </div>
      )}
      {downloadable && (
        <button
          type="button"
          className="brand-tile-canvas-action brand-tile-download"
          title={`Download ${label}`}
          aria-label={`Download ${label}`}
          onClick={() => download(url, label)}
        >
          <i className="bi bi-download"></i>
        </button>
      )}
      <button
        type="button"
        className="brand-tile-canvas-action brand-tile-backdrop"
        title={onDark ? "Preview on light" : "Preview on dark"}
        aria-label={onDark ? "Preview on light background" : "Preview on dark background"}
        onClick={() => setDarkOverride(!onDark)}
      >
        <i className={`bi ${onDark ? "bi-brightness-high" : "bi-moon-stars"}`}></i>
      </button>
    </div>
  );
};

export default BrandAssetPreview;
